import { FC } from 'react';
import { HiOutlinePencilAlt, HiOutlineTrash, HiOutlineExternalLink } from 'react-icons/hi';
import { DAV_MAIN_DOMAIN, PostProps } from '../../../../constants';
import { Card } from '../../../ui/Card';

interface PostCardProps {
  post: PostProps;
  layout?: 'grid' | 'list';
  onEditHandler: () => void;
  onDeleteHandler: () => void;
  isLanguage?: boolean;
}

const PostCard: FC<PostCardProps> = ({ post, layout = 'grid', onEditHandler, onDeleteHandler, isLanguage }) => {
  const { name, slug, postImage, createdAt } = post;
  const isList = layout === 'list';

  const handleView = () => {
    window.open(
      `${DAV_MAIN_DOMAIN}/safari-updates${isLanguage ? '/languages' : ''}/${slug}`,
      '_blank',
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleView();
    }
  };

  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }) : null;

  return (
    <Card 
      variant='glass' 
      className={`
        group relative overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none 
        transition-all duration-500 hover:shadow-2xl hover:-translate-y-1
        ${isList ? 'h-full sm:h-56' : 'h-full flex flex-col'}
      `}
    >
      <div className={`flex flex-col ${isList ? 'sm:flex-row h-full' : 'h-full'}`}>
        {/* Image Container */}
        <div 
          onClick={handleView}
          onKeyDown={handleKeyDown}
          role='button'
          tabIndex={0}
          className={`relative flex-shrink-0 flex-grow-0 overflow-hidden cursor-pointer ${isList ? 'w-full sm:w-72 h-48 sm:h-full' : 'w-full h-56'}`}
        >
          <img 
          src={postImage} 
          alt={name} 
          className='w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110' 
        />
        
        {/* Overlays */}
        <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500' />
        
        {/* Floating Badge */}
        <div className='absolute top-4 left-4 z-10'>
          <span className={`
             px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-md border
             ${isLanguage ?
               'bg-blue-500/20 text-blue-100 border-blue-400/30'
               : 'bg-primary-500/20 text-primary-100 border-primary-400/30'}
          `}>
            {isLanguage ? 'Language Update' : 'Safari Post'}
          </span>
        </div>

        {/* External Link Icon */}
        <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-4 group-hover:translate-y-0'>
          <div className='bg-white/20 backdrop-blur-md p-3 rounded-full border border-white/30'>
             <HiOutlineExternalLink className='w-6 h-6 text-white' />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className='flex flex-col flex-1 p-6 bg-white/50 dark:bg-gray-800/10'>
        {/* Date */}
        {formattedDate && (
          <div className='flex items-center gap-2 mb-3'>
            <div className='w-1 h-1 rounded-full bg-primary-500' />
            <span className='text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest'>
              {formattedDate}
            </span>
          </div>
        )}

        {/* Title */}
        <h3 
          className='text-xl font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight group-hover:text-primary-600 transition-colors mb-4'
        >
          <span
            onClick={handleView}
            onKeyDown={handleKeyDown}
            role='button'
            tabIndex={0}
            className='cursor-pointer outline-none'
          >
            {name}
          </span>
        </h3>

        {/* Footer Actions */}
        <div className='mt-auto pt-6 flex items-center justify-between border-t border-gray-100 dark:border-gray-700/50'>
          <button
            type='button'
            onClick={handleView}
            className='text-sm font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors flex items-center gap-1.5 group/link'
          >
            Read More
            <HiOutlineExternalLink className='w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5' />
          </button>

          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={(e) => { e.stopPropagation(); onEditHandler(); }}
              className='p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/20 dark:hover:text-primary-400 transition-all'
              title='Edit Update'
            >
              <HiOutlinePencilAlt className='w-5 h-5' />
            </button>
            <button
              type='button'
              onClick={(e) => { e.stopPropagation(); onDeleteHandler(); }}
              className='p-2.5 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400 hover:bg-rose-100 hover:text-rose-700 dark:hover:bg-rose-900/30 transition-all'
              title='Delete Update'
            >
              <HiOutlineTrash className='w-5 h-5' />
            </button>
          </div>
        </div>
      </div>
      </div>
    </Card>
  );
};


export default PostCard;
